import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetContactPageFormSubmissions as GetContactPageFormSubmissionsQuery,
    GetContactPageFormSubmissions_contact_submissions as Submissions,
    GetContactPageFormSubmissionsVariables,
} from 'nutshell-graphql-types';

import GET_CONTACT_PAGE_FORM_SUBMISSIONS from '../queries/get-contact-page-form-submissions.graphql';

const INITIAL_VISIBLE_SUBMISSIONS = 5;

export function useGetContactPageFormSubmissions(contactId: string): {
    submissions: Submissions | null;
    totalCount: number;
    isLoading: boolean;
    isFetchingMore: boolean;
    hasNextPage: boolean;
    error: string | null | undefined;
    refetch: () => Promise<any>;
    fetchAllSubmissions: () => Promise<any>;
} {
    const {data, loading, error, refetch, fetchMore} = useQuery<
        GetContactPageFormSubmissionsQuery,
        GetContactPageFormSubmissionsVariables
    >(GET_CONTACT_PAGE_FORM_SUBMISSIONS, {
        variables: {id: contactId, first: INITIAL_VISIBLE_SUBMISSIONS},
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const submissions = data?.contact?.submissions ?? null;
    const totalCount = submissions?.totalCount ?? 0;
    const currentCount = submissions?.edges.length ?? 0;
    const hasNextPage = submissions?.pageInfo?.hasNextPage ?? false;

    const fetchAllSubmissions = () => {
        if (!hasNextPage || !submissions?.pageInfo?.endCursor || currentCount >= totalCount) {
            return Promise.resolve();
        }

        return fetchMore({
            variables: {
                id: contactId,
                after: submissions.pageInfo.endCursor,
                first: totalCount - currentCount,
            },
            updateQuery: (
                prev: GetContactPageFormSubmissionsQuery,
                {
                    fetchMoreResult,
                }: {
                    fetchMoreResult?: GetContactPageFormSubmissionsQuery;
                }
            ) => {
                if (!fetchMoreResult?.contact?.submissions || !prev.contact?.submissions) {
                    return prev;
                }

                return {
                    ...fetchMoreResult,
                    contact: {
                        ...fetchMoreResult.contact,
                        submissions: {
                            ...fetchMoreResult.contact.submissions,
                            edges: [
                                ...prev.contact.submissions.edges,
                                ...fetchMoreResult.contact.submissions.edges,
                            ],
                        },
                    },
                };
            },
        });
    };

    return {
        submissions,
        totalCount,
        isLoading: loading && !data,
        isFetchingMore: loading && Boolean(data),
        hasNextPage,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        refetch,
        fetchAllSubmissions,
    };
}
